<script setup lang="tsx">
import { ref } from 'vue'
import http from '@/services/http'
import UserFilters from './UserFilters.vue'
import OrderLabel from '@/components/widgets/OrderLabel.vue'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import useUserColumns from './columns'
import columnStore from '@/stores/column'
import type { PageRequest, PageResponse, Response, User } from '@/models/api-generated'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import CustomHeader from '../custom_field/CustomHeader.vue'
import { fetchCustomValuesForPage, type CustomValueMap } from '@/services/custom'
import sliderStore from '@/stores/slider'
import { refreshCallback } from './shared'
import type { UserFilter } from '@/models/filters-generated'
import InstanceList from './InstanceList.vue'

const props = defineProps({
	component: { default: 'users' },
})

const slider = sliderStore()
const model = defineModel<PageResponse<User>>()
const customValues = ref<CustomValueMap>()
const isLoading = ref(false)
const { columns, custom } = useUserColumns(props.component)
const root = columnStore().defineComponent(props.component, fetch)
refreshCallback.value = root.triggerFetch

async function fetch(signal: AbortSignal) {
	const request: PageRequest<UserFilter> = {
		page: root.pagination.page,
		limit: root.pagination.limit,
		filter: root.filterValue() as UserFilter,
		orderBy: root.orderValue(),
	}

	isLoading.value = true
	const { data } = await http.post<Response<PageResponse<User>>>(`api/user/list`, request, { signal })
	customValues.value = await fetchCustomValuesForPage('users', data)
	isLoading.value = false

	if (data.data) model.value = data.data
}

function rowClick(user: User) {
	slider.openUser(user.id, `${user.firstname} ${user.lastname}`)
}
</script>

<template>
	<UserFilters :component="props.component"></UserFilters>

	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3">
		<div class="table-responsive w-100">
			<table class="table">
				<thead>
					<tr>
						<th v-if="columns.instance.value.isVisible">
							<OrderLabel v-model="columns.instance.value">{{ $t('instance') }}</OrderLabel>
						</th>
						<th v-if="columns.firstname.value.isVisible">
							<OrderLabel v-model="columns.firstname.value">{{ $t('firstname') }}</OrderLabel>
						</th>
						<th v-if="columns.lastname.value.isVisible">
							<OrderLabel v-model="columns.lastname.value">{{ $t('lastname') }}</OrderLabel>
						</th>
						<th v-if="columns.idCode.value.isVisible">
							<OrderLabel v-model="columns.idCode.value">{{ $t('idCode') }}</OrderLabel>
						</th>
						<th v-if="columns.email.value.isVisible">
							<OrderLabel v-model="columns.email.value">{{ $t('email') }}</OrderLabel>
						</th>
						<th v-if="columns.aduser.value.isVisible">
							<OrderLabel v-model="columns.aduser.value">{{ $t('adUser') }}</OrderLabel>
						</th>
						<th v-if="columns.active.value.isVisible">
							<OrderLabel v-model="columns.active.value">{{ $t('active') }}</OrderLabel>
						</th>
						<th v-if="columns.roles.value.isVisible">
							{{ $t('role', 2) }}
						</th>
						<CustomHeader :columns="custom"></CustomHeader>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(user, i) in model?.items"
						@mouseup.prevent.stop="rowClick(user)"
						:class="{ odd: i % 2 != 0 }"
						:key="user.id"
						class="user-row"
					>
						<td v-if="columns.instance.value.isVisible">
							<InstanceList :instances="user.instances" />
						</td>
						<td v-if="columns.firstname.value.isVisible">
							{{ user.firstname }}
						</td>
						<td v-if="columns.lastname.value.isVisible">
							{{ user.lastname }}
						</td>
						<td v-if="columns.idCode.value.isVisible">
							{{ user.idCode }}
						</td>
						<td v-if="columns.email.value.isVisible">
							{{ user.email }}
						</td>
						<td v-if="columns.aduser.value.isVisible">
							{{ user.adUser }}
						</td>
						<td v-if="columns.active.value.isVisible">
							<i v-if="user.active" class="bi bi-circle-fill status-normal-background"></i>
							<i v-else class="bi bi-circle-fill status-verybig-background"></i>
						</td>
						<td v-if="columns.roles.value.isVisible">
							<template v-if="user.roles.length">
								{{ user.roles.map((x) => x.name).join(', ') }}
							</template>
							<template v-else>-</template>
						</td>

						<CustomRow :columns="custom" v-model="customValues" :row-id="user.id"></CustomRow>
					</tr>
				</tbody>
			</table>
		</div>
	</LoadingContainer>
	<Pagination v-model="root.pagination" :total-pages="model?.pages" :tableTitle="props.component" />
</template>

<style lang="sass" scoped>
td:hover
    cursor: pointer
</style>
